import { promiseWrapper } from '@/helpers';
import { SearchType } from '@/hooks/search/useSearch.helpers';
import { autocompleteService } from '@/services';
import { AutoSuggestionSection } from '../shared/types';
import { KeywordSuggestion } from './KeywordAutoSuggest.type';

const LOCAL_STORAGE_SAVED_KEYWORD_SUGGESTIONS_KEY = 'savedKeywordSuggestions';
const VISIBLE_HISTORY_LIMIT = 3;

export function getSavedSuggestionValues(): string[] {
  try {
    const saved = localStorage.getItem(LOCAL_STORAGE_SAVED_KEYWORD_SUGGESTIONS_KEY);
    const parsed = saved ? JSON.parse(saved) : [];
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

export function buildSuggestionSections({
  matches,
  rest,
  historySuggestions,
}: {
  matches: AutoSuggestionSection<KeywordSuggestion>[];
  rest: AutoSuggestionSection<KeywordSuggestion>[];
  historySuggestions: KeywordSuggestion[];
}): {
  suggestions: AutoSuggestionSection<KeywordSuggestion>[];
  storedSuggestions: AutoSuggestionSection<KeywordSuggestion>[];
  hasHistory: boolean;
} {
  const hasHistory = historySuggestions.length > 0;
  const historyFull: AutoSuggestionSection<KeywordSuggestion> = {
    type: 'history',
    title: 'history',
    suggestions: historySuggestions,
  };
  const historyVisible: AutoSuggestionSection<KeywordSuggestion> = {
    ...historyFull,
    suggestions: historySuggestions.slice(0, VISIBLE_HISTORY_LIMIT),
  };

  const suggestions = [...matches, ...(hasHistory ? [historyVisible] : []), ...rest];
  const storedSuggestions = [...matches, ...(hasHistory ? [historyFull] : []), ...rest];

  return { suggestions, storedSuggestions, hasHistory };
}

export async function getSuggestionsFromSearchValue(
  value: string,
  searchType: SearchType,
  signal?: AbortSignal,
): Promise<{
  suggestions: AutoSuggestionSection<KeywordSuggestion>[];
  storedSuggestions: AutoSuggestionSection<KeywordSuggestion>[];
  newServices: AutoSuggestionSection<KeywordSuggestion>[];
  hasHistory: boolean;
}> {
  const { data, error } = await promiseWrapper(autocompleteService.getSuggestions(value, searchType, signal));

  const fetched: AutoSuggestionSection<KeywordSuggestion>[] =
    !error && data && Array.isArray(data.suggestions) ? data.suggestions : [];
  const newServices: AutoSuggestionSection<KeywordSuggestion>[] = !error && data?.newServices ? data.newServices : [];

  const historySuggestions: KeywordSuggestion[] = getSavedSuggestionValues().map((text) => ({
    type: 'history',
    text,
  }));
  const matches = fetched.filter((suggestion) => suggestion.type === 'match');
  const rest = fetched.filter((suggestion) => suggestion.type !== 'match');

  const built = buildSuggestionSections({ matches, rest, historySuggestions });

  return { ...built, newServices };
}

export function saveSuggestionValue(value: string) {
  const savedValues = getSavedSuggestionValues();
  const valueExists = savedValues.some((v: string) => v.toLowerCase() === value.toLowerCase());

  if (valueExists) {
    return;
  }

  const newSavedValues = [value, ...savedValues.filter((v: string) => v.toLowerCase() !== value.toLowerCase())].slice(
    0,
    5,
  );
  localStorage.setItem(LOCAL_STORAGE_SAVED_KEYWORD_SUGGESTIONS_KEY, JSON.stringify(newSavedValues));
}

export function removeSavedSuggestionValue(value: string) {
  const savedValues = getSavedSuggestionValues();
  const newSavedValues = savedValues.filter((v: string) => v.toLowerCase() !== value.toLowerCase());
  localStorage.setItem(LOCAL_STORAGE_SAVED_KEYWORD_SUGGESTIONS_KEY, JSON.stringify(newSavedValues));
}
